import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';
import { http } from '@/lib/ats-https';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Merge-variable map from `GET /api_ats/v1/ats_comm_templates/variables`.
 * Keys are category names (e.g. `org`, `candidate`, `job`), values are variable name arrays.
 */
export type AtsCommTemplateVariables = Partial<Record<string, string[]>>;

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

export async function fetchAtsCommTemplateVariables(
  views: readonly string[]
): Promise<AtsCommTemplateVariables> {
  const params = new URLSearchParams();
  for (const v of views) {
    params.append('view[]', v);
  }
  params.set('items', '200');

  const res = await http.get(
    `/ats_comm_templates/variables?${params.toString()}`
  );
  assertResolvedAtsNotHttpError(res);

  const variables =
    (res as { variables?: AtsCommTemplateVariables })?.variables ??
    (res as { data?: { variables?: AtsCommTemplateVariables } })?.data
      ?.variables;

  return variables && typeof variables === 'object' && !Array.isArray(variables)
    ? variables
    : {};
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const variablesQueryKeys = {
  all: ['ats-comm-templates', 'variables'] as const,
  byViews: (views: readonly string[]) =>
    [...variablesQueryKeys.all, ...views] as const,
};

export function useAtsCommTemplateVariables(
  views: readonly string[]
): UseQueryResult<AtsCommTemplateVariables, Error> {
  return useQuery({
    queryKey: variablesQueryKeys.byViews(views),
    queryFn: () => fetchAtsCommTemplateVariables(views),
    staleTime: 2 * 60_000,
    enabled: views.length > 0,
  });
}
